import React from "react";
import useOpportunity from "../hooks/useOpportunity";
import useUploadFiles from "../../../hooks/useUploadFiles";
import Upload from "../../../component/modals/upload";
import DocumentUpload from "../../../component/modals/upload/DocumentUpload";
import TextBox from "../../../component/element/forms/TextBox";
import TextInput from "../../../component/element/forms/TextInput";
import DateInput from "../../../component/element/forms/DateInput";
import SelectOption from "../../../component/element/forms/SelectOption";
import Modal from "../../../component/ui/modal";
import { useAppDispatch, useAppSelector } from "../../../store";
import { edit_opportunity } from "../../../services/apis/opportunity";
import {
  ICreateOpportunityError,
  IOpportunity,
} from "../../../store/types/opportunity";
import { Image } from "lucide-react";

type EditOpportunityProps = {
  show: boolean;
  opportunity: IOpportunity;
  onHandler: () => void;
};

type UploadedFile = {
  id: string;
  url: string;
  path: string;
};

const EditOportunity: React.FC<EditOpportunityProps> = ({
  show,
  opportunity,
  onHandler,
}) => {
  const dispatch = useAppDispatch();
  const types = useAppSelector((state) => state.opportunity.opportunity_types);
  const loading = useAppSelector((state) => state.opportunity.loading);
  const { data, error, dataSet, errorSet } = useOpportunity(opportunity);
  const {
    loader: imageLoading,
    uploadFile: uploadImage,
    deleteFiles: deleteImage,
  } = useUploadFiles();
  const {
    loader: filesLoading,
    uploadFile: uploadDocuments,
    deleteFiles: deleteDocument,
  } = useUploadFiles();
  const [imagePath, setImagePath] = React.useState("");
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);

  React.useEffect(() => {
    setUploadedFiles([]);
    setImagePath("");
  }, [opportunity.id]);

  const handleSubmit = async () => {
    const required = "error: this field is required" as const;
    const errors: ICreateOpportunityError = {
      opportunity_name: data.opportunity_name.trim() ? "" : required,
      opportunity_files: data.opportunity_files.trim() ? "" : required,
      opportunity_description: data.opportunity_description.trim()
        ? ""
        : required,
      expire_date: data.expire_date.trim() ? "" : required,
      opportunity_image: data.opportunity_image ? "" : required,
      opportunity_type: data.opportunity_type ? "" : required,
    };
    errorSet(errors);
    if (Object.values(errors).some(Boolean)) return;

    if (await edit_opportunity(data, dispatch)) onHandler();
  };

  return (
    <Modal
      title="Edit Opportunity"
      open={show}
      onClose={onHandler}
      onOkClick={handleSubmit}
      isLoading={loading || imageLoading || filesLoading}
    >
      <div className="space-y-6">
        <Upload
          icon={<Image />}
          title="Upload Image"
          label="JPG, PNG"
          name={`opportunity_image_${opportunity.id}`}
          loading={imageLoading}
          status={error.opportunity_image}
          completed={Boolean(data.opportunity_image)}
          imgPreview={data.opportunity_image}
          onChange={async (file) => {
            const uploaded = await uploadImage(
              file,
              "opportunity_image",
              "image",
            );
            if (uploaded.length) {
              dataSet((previous) => ({
                ...previous,
                opportunity_image: uploaded[0].file_url,
              }));
              errorSet((previous) => ({ ...previous, opportunity_image: "" }));
              setImagePath(uploaded[0].path);
            }
          }}
          onDelete={async () => {
            if (imagePath && !(await deleteImage(imagePath))) return;
            dataSet((previous) => ({ ...previous, opportunity_image: "" }));
            setImagePath("");
          }}
        />

        <DocumentUpload
          title="Add Documents"
          name={`opportunity_documents_${opportunity.id}`}
          maxFiles={1}
          onChange={async (items) => {
            const item = items[0];
            if (!item) return;
            const uploaded = await uploadDocuments(
              item.file,
              "opportunity_file",
              "document",
            );
            if (!uploaded.length) return;
            const newFile = {
              id: item.id,
              url: uploaded[0].file_url,
              path: uploaded[0].path,
            };
            setUploadedFiles([newFile]);
            dataSet((previous) => ({
              ...previous,
              opportunity_files: newFile.url,
            }));
            errorSet((previous) => ({ ...previous, opportunity_files: "" }));
          }}
          onDelete={async (fileId) => {
            const file = uploadedFiles.find((item) => item.id === fileId);
            if (!file || !(await deleteDocument(file.path))) return;
            setUploadedFiles((previous) =>
              previous.filter((item) => item.id !== fileId),
            );
            dataSet((previous) => ({
              ...previous,
              opportunity_files:
                previous.opportunity_files === file.url
                  ? ""
                  : previous.opportunity_files,
            }));
          }}
        />
        {data.opportunity_files &&
          !uploadedFiles.some(
            (uploaded) => uploaded.url === data.opportunity_files,
          ) && (
            <div
              key={data.opportunity_files}
              className="flex items-center justify-between text-sm"
            >
              <a
                href={data.opportunity_files}
                target="_blank"
                rel="noreferrer"
                className="truncate text-primary-600"
              >
                Existing document
              </a>
              <button
                type="button"
                onClick={() =>
                  dataSet((previous) => ({
                    ...previous,
                    opportunity_files: "",
                  }))
                }
                className="ml-3 text-red-600"
              >
                Remove
              </button>
            </div>
          )}
        {error.opportunity_files && (
          <p className="text-xs text-red-500">Add at least one document</p>
        )}

        <TextInput
          label="Opportunity Name"
          name="opportunity_name"
          placeholder="Opportunity Name"
          type="text"
          value={data.opportunity_name}
          status={error.opportunity_name}
          onChange={(event) => {
            dataSet((previous) => ({
              ...previous,
              opportunity_name: event.target.value,
            }));
            errorSet((previous) => ({ ...previous, opportunity_name: "" }));
          }}
        />
        <SelectOption
          items={types}
          label="Opportunity Type"
          placeholder="Select Type"
          value={data.opportunity_type ? String(data.opportunity_type) : ""}
          onChange={(opportunity_type) => {
            dataSet((previous) => ({
              ...previous,
              opportunity_type: opportunity_type ? Number(opportunity_type) : 0,
            }));
            errorSet((previous) => ({ ...previous, opportunity_type: "" }));
          }}
        />
        {error.opportunity_type && (
          <p className="text-xs text-red-500">Select an opportunity type</p>
        )}
        <DateInput
          label="Select Expire Date"
          value={data.expire_date}
          error={error.expire_date ? "Expiration date is required" : undefined}
          onChange={(expire_date) => {
            dataSet((previous) => ({ ...previous, expire_date }));
            errorSet((previous) => ({ ...previous, expire_date: "" }));
          }}
        />
        <TextBox
          label="Opportunity Description"
          name="opportunity_description"
          value={data.opportunity_description}
          placeholder="Opportunity Description"
          onChange={(name, value) => {
            dataSet((previous) => ({ ...previous, [name]: value }));
            errorSet((previous) => ({
              ...previous,
              opportunity_description: "",
            }));
          }}
        />
      </div>
    </Modal>
  );
};

export default EditOportunity;
